/**
 * MANAGEMENT (15) · Analyzer-Fleet screen i18n (FR default + AR-RTL). ADDITIVE to the shell `../messages.ts`
 * (which owns the `mgmt.unavailable.*` notice + the nav section label) — this registers ONLY the NEW keys the
 * fleet table + the filters + the health drill-down drawer + its charts need, so it never collides with the
 * shell dictionary and the shell file stays untouched. `registerMessages` is `Object.assign` (idempotent);
 * the screen calls `registerAnalyzersMessages()` at module-eval (a `registered` latch makes it a no-op after
 * the first).
 *
 * The nav LABEL key (`nav.mgmt.analyzers`) is registered here too so the nav item added in index.tsx (the
 * INTEGRATE hookup) resolves in both locales without touching the shell messages file.
 *
 * Codes / disciplines / protocols / durations render inside `.nv-ltr-island` so figures read identically
 * under dir="rtl". A missing AR key falls back to the FR value, then the raw key (never a blank, never a crash).
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // ── nav (the INTEGRATE hookup label — registered here to keep the shell messages untouched) ──
  "nav.mgmt.analyzers": "Parc d'automates",

  // ── page header ──────────────────────────────────────────────────────────────
  "mgmt.analyzers.title": "Parc d'automates",
  "mgmt.analyzers.subtitle":
    "Surveillance transversale du parc d'automates de tous les laboratoires — santé, disponibilité, usage.",

  // ── filters ──────────────────────────────────────────────────────────────────
  "mgmt.analyzers.search": "Rechercher (code, libellé, fabricant, modèle)…",
  "mgmt.analyzers.filter.discipline": "Discipline (code)",
  "mgmt.analyzers.filter.statut": "Statut",
  "mgmt.analyzers.filter.statut.all": "Tous les statuts",
  "mgmt.analyzers.filter.online": "Disponibilité",
  "mgmt.analyzers.filter.online.all": "Toutes disponibilités",
  "mgmt.analyzers.filter.online.online": "En ligne",
  "mgmt.analyzers.filter.online.offline": "Hors ligne",

  // ── columns ──────────────────────────────────────────────────────────────────
  "mgmt.analyzers.col.lab": "Laboratoire",
  "mgmt.analyzers.col.model": "Automate",
  "mgmt.analyzers.col.discipline": "Discipline",
  "mgmt.analyzers.col.protocol": "Protocole",
  "mgmt.analyzers.col.statut": "Statut",
  "mgmt.analyzers.col.lastSeen": "Dernière présence",
  "mgmt.analyzers.col.health": "Santé",
  "mgmt.analyzers.col.actions": "Actions",
  "mgmt.analyzers.protocol.unconfigured": "Non configuré",
  "mgmt.analyzers.action.health": "Voir la santé",

  // ── statut vocabulary (mirrors StatutAutomate) ─────────────────────────────────
  "mgmt.analyzers.statut.actif": "Actif",
  "mgmt.analyzers.statut.maintenance": "Maintenance",
  "mgmt.analyzers.statut.hors_service": "Hors service",

  // ── heartbeat / health chip ────────────────────────────────────────────────────
  "mgmt.analyzers.heartbeat.online": "En ligne",
  "mgmt.analyzers.heartbeat.stale": "Silencieux",
  "mgmt.analyzers.heartbeat.noAgent": "Aucun agent",

  // ── insight strip (total = fleet-wide server count; heartbeat chips = current page) ────────────
  "mgmt.analyzers.insight.title": "Parc d'automates",
  "mgmt.analyzers.insight.total": "Automates (total)",
  "mgmt.analyzers.insight.online": "En ligne (page)",
  "mgmt.analyzers.insight.offline": "Silencieux (page)",
  "mgmt.analyzers.insight.down": "Hors service (page)",

  // ── empty states ─────────────────────────────────────────────────────────────
  "mgmt.analyzers.empty.title": "Aucun automate",
  "mgmt.analyzers.empty.desc": "Aucun automate n'est encore déclaré sur le parc.",
  "mgmt.analyzers.empty.search": "Aucun automate ne correspond à ce filtre.",

  // ── drill-down drawer ──────────────────────────────────────────────────────────
  "mgmt.analyzers.drawer.title": "Santé de l'automate — {code}",
  "mgmt.analyzers.drawer.meta": "Identité & transport",
  "mgmt.analyzers.meta.code": "Code",
  "mgmt.analyzers.meta.transport": "Transport",
  "mgmt.analyzers.window.label": "Fenêtre d'analyse",
  "mgmt.analyzers.window.days": "{n} j",

  // ── drill-down KPI chips ───────────────────────────────────────────────────────
  "mgmt.analyzers.kpi.results": "Résultats",
  "mgmt.analyzers.kpi.errors": "Erreurs",
  "mgmt.analyzers.kpi.errorRate": "Taux d'erreur",
  "mgmt.analyzers.kpi.tat": "TAT moyen",
  "mgmt.analyzers.kpi.uptime": "Disponibilité",
  "mgmt.analyzers.kpi.snapshotDays": "Jours mesurés",

  // ── charts ─────────────────────────────────────────────────────────────────────
  "mgmt.analyzers.chart.empty.title": "Pas encore de données",
  "mgmt.analyzers.chart.empty.desc":
    "Aucun instantané de santé n'a encore été enregistré pour cette fenêtre.",
  "mgmt.analyzers.chart.volume.title": "Volume traité (résultats vs erreurs)",
  "mgmt.analyzers.chart.volume.results": "Résultats",
  "mgmt.analyzers.chart.volume.errors": "Erreurs",
  "mgmt.analyzers.chart.volume.aria": "Volume traité par jour, total {total} enregistrements.",
  "mgmt.analyzers.chart.tat.title": "Temps de traitement moyen (TAT)",
  "mgmt.analyzers.chart.tat.tip": "TAT moyen",
  "mgmt.analyzers.chart.tat.aria": "Temps de traitement moyen par jour.",
  "mgmt.analyzers.chart.uptime.title": "Disponibilité quotidienne",
  "mgmt.analyzers.chart.uptime.tip": "Disponibilité",
  "mgmt.analyzers.chart.uptime.aria": "Disponibilité quotidienne en pourcentage.",
};

const AR: Record<string, string> = {
  // ── nav ──────────────────────────────────────────────────────────────────────
  "nav.mgmt.analyzers": "أسطول الأجهزة",

  // ── page header ──────────────────────────────────────────────────────────────
  "mgmt.analyzers.title": "أسطول الأجهزة",
  "mgmt.analyzers.subtitle":
    "مراقبة شاملة لأجهزة جميع المختبرات — الصحة، التوفّر، الاستخدام.",

  // ── filters ──────────────────────────────────────────────────────────────────
  "mgmt.analyzers.search": "ابحث (الرمز، التسمية، الصانع، الطراز)…",
  "mgmt.analyzers.filter.discipline": "التخصص (الرمز)",
  "mgmt.analyzers.filter.statut": "الحالة",
  "mgmt.analyzers.filter.statut.all": "جميع الحالات",
  "mgmt.analyzers.filter.online": "التوفّر",
  "mgmt.analyzers.filter.online.all": "كل حالات التوفّر",
  "mgmt.analyzers.filter.online.online": "متصل",
  "mgmt.analyzers.filter.online.offline": "غير متصل",

  // ── columns ──────────────────────────────────────────────────────────────────
  "mgmt.analyzers.col.lab": "المختبر",
  "mgmt.analyzers.col.model": "الجهاز",
  "mgmt.analyzers.col.discipline": "التخصص",
  "mgmt.analyzers.col.protocol": "البروتوكول",
  "mgmt.analyzers.col.statut": "الحالة",
  "mgmt.analyzers.col.lastSeen": "آخر ظهور",
  "mgmt.analyzers.col.health": "الصحة",
  "mgmt.analyzers.col.actions": "إجراءات",
  "mgmt.analyzers.protocol.unconfigured": "غير مُهيّأ",
  "mgmt.analyzers.action.health": "عرض الصحة",

  // ── statut vocabulary ──────────────────────────────────────────────────────────
  "mgmt.analyzers.statut.actif": "نشط",
  "mgmt.analyzers.statut.maintenance": "صيانة",
  "mgmt.analyzers.statut.hors_service": "خارج الخدمة",

  // ── heartbeat / health chip ────────────────────────────────────────────────────
  "mgmt.analyzers.heartbeat.online": "متصل",
  "mgmt.analyzers.heartbeat.stale": "صامت",
  "mgmt.analyzers.heartbeat.noAgent": "لا يوجد وكيل",

  // ── insight strip ──────────────────────────────────────────────────────────────
  "mgmt.analyzers.insight.title": "أسطول الأجهزة",
  "mgmt.analyzers.insight.total": "الأجهزة (الإجمالي)",
  "mgmt.analyzers.insight.online": "متصلة (الصفحة)",
  "mgmt.analyzers.insight.offline": "صامتة (الصفحة)",
  "mgmt.analyzers.insight.down": "خارج الخدمة (الصفحة)",

  // ── empty states ─────────────────────────────────────────────────────────────
  "mgmt.analyzers.empty.title": "لا توجد أجهزة",
  "mgmt.analyzers.empty.desc": "لم يُسجَّل أي جهاز في الأسطول بعد.",
  "mgmt.analyzers.empty.search": "لا يوجد جهاز يطابق هذا المرشّح.",

  // ── drill-down drawer ──────────────────────────────────────────────────────────
  "mgmt.analyzers.drawer.title": "صحة الجهاز — {code}",
  "mgmt.analyzers.drawer.meta": "الهوية والنقل",
  "mgmt.analyzers.meta.code": "الرمز",
  "mgmt.analyzers.meta.transport": "النقل",
  "mgmt.analyzers.window.label": "نافذة التحليل",
  "mgmt.analyzers.window.days": "{n} يوم",

  // ── drill-down KPI chips ───────────────────────────────────────────────────────
  "mgmt.analyzers.kpi.results": "النتائج",
  "mgmt.analyzers.kpi.errors": "الأخطاء",
  "mgmt.analyzers.kpi.errorRate": "معدّل الخطأ",
  "mgmt.analyzers.kpi.tat": "متوسّط زمن المعالجة",
  "mgmt.analyzers.kpi.uptime": "التوفّر",
  "mgmt.analyzers.kpi.snapshotDays": "الأيام المقيسة",

  // ── charts ─────────────────────────────────────────────────────────────────────
  "mgmt.analyzers.chart.empty.title": "لا توجد بيانات بعد",
  "mgmt.analyzers.chart.empty.desc": "لم تُسجَّل أي لقطة صحية لهذه النافذة بعد.",
  "mgmt.analyzers.chart.volume.title": "الحجم المعالَج (النتائج مقابل الأخطاء)",
  "mgmt.analyzers.chart.volume.results": "النتائج",
  "mgmt.analyzers.chart.volume.errors": "الأخطاء",
  "mgmt.analyzers.chart.volume.aria": "الحجم المعالَج يوميًا، الإجمالي {total} سجل.",
  "mgmt.analyzers.chart.tat.title": "متوسّط زمن المعالجة (TAT)",
  "mgmt.analyzers.chart.tat.tip": "متوسّط زمن المعالجة",
  "mgmt.analyzers.chart.tat.aria": "متوسّط زمن المعالجة يوميًا.",
  "mgmt.analyzers.chart.uptime.title": "التوفّر اليومي",
  "mgmt.analyzers.chart.uptime.tip": "التوفّر",
  "mgmt.analyzers.chart.uptime.aria": "التوفّر اليومي بالنسبة المئوية.",
};

let registered = false;
export function registerAnalyzersMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
